import BaseClient from './BaseClient';
import { formatDate } from '@app/utilities';

export default class CustomerInformationSystemClient extends BaseClient {
  constructor(tenantId: string) {
    super(null, tenantId, true, 'cus');
  }

  public async createCustomer(externalId, title, givenName, middleName, familyName, suffix, dateOfBirth) {
    const response = await this.post(`/api/proxy/cus/customer-information-service/customers`, {
      externalId,
      title,
      givenName,
      middleName,
      familyName,
      suffix,
      dateOfBirth: formatDate(dateOfBirth, 'yyyy-MM-dd'),
    });
    return await this.handleResponse(response);
  }

  public async updateCustomer(customerId, externalId, title, givenName, middleName, familyName, suffix, dateOfBirth) {
    const response = await this.put(`/api/proxy/cus/customer-information-service/customers/${customerId}`, {
      externalId,
      title,
      givenName,
      middleName,
      familyName,
      suffix,
      dateOfBirth,
    });
    return await this.handleResponse(response);
  }

  public async associateAccount(customerId: string, accountId: string): Promise<CustomerSearchResult[]> {
    const response = await this.post(
      `/api/proxy/cus/customer-information-service/customers/${customerId}/account-association`,
      {
        accountId,
        externalId: `${customerId}-link`,
      }
    );
    return await this.handleResponse<CustomerSearchResult[]>(response);
  }

  public async getCustomerById(customerId: string) {
    const response = await this.get(`/api/proxy/cus/customer-information-service/customers/${customerId}`);
    return await this.handleResponse<any>(response);
  }

  public async getCustomerByAccountNumber(accountNumber: string) {
    const response = await this.get(`/api/proxy/cus/customer-information-service/customers/accounts/${accountNumber}`);
    return await this.handleResponse<any>(response);
  }

  public async getAddresses(customerId: string): Promise<CustomerAddress[]> {
    const response = await this.get(`/api/proxy/cus/customer-information-service/customers/${customerId}/addresses`);
    const { addresses } = await this.handleResponse<{ addresses: CustomerAddress[] }>(response);
    return addresses;
  }

  public async getAddressById(customerId: string, addressId: string): Promise<CustomerAddress> {
    const response = await this.get(
      `/api/proxy/cus/customer-information-service/customers/${customerId}/addresses/${addressId}`
    );
    return await this.handleResponse<CustomerAddress>(response);
  }

  public async getAccounts(customerId: string): Promise<CustomerAccount[]> {
    const response = await this.get(`/api/proxy/cus/customer-information-service/customers/${customerId}/accounts`);
    const { accounts } = await this.handleResponse<{ accounts: CustomerAccount[] }>(response);
    return accounts;
  }

  public async search(givenName: string, familyName: string, postalCode: string): Promise<CustomerSearchResult[]> {
    const response = await this.post(`/api/proxy/cus/customer-information-service/customers/search`, {
      givenName,
      familyName,
      postalCode,
    });
    return await this.handleResponse<CustomerSearchResult[]>(response);
  }

  public async createAddress(
    customerId: string,
    houseNumber: string,
    houseName: string,
    flatNumber: string,
    street: string,
    locality: string,
    townOrCity: string,
    county: string,
    postalCode: string,
    countryCode: string
  ) {
    const response = await this.post(`/api/proxy/cus/customer-information-service/customers/${customerId}/addresses`, {
      format: 'GBR',
      address: {
        houseNumber,
        houseName,
        flatNumber,
        street,
        locality,
        townOrCity,
        county,
        postalCode,
        countryCode,
      },
    });
    return await this.handleResponse(response);
  }

  public async updateAddress(
    customerId: string,
    addressId: string,
    houseNumber: string,
    houseName: string,
    flatNumber: string,
    street: string,
    locality: string,
    townOrCity: string,
    county: string,
    postalCode: string,
    countryCode: string
  ) {
    const response = await this.put(
      `/api/proxy/cus/customer-information-service/customers/${customerId}/addresses/${addressId}`,
      {
        address: {
          houseNumber,
          houseName,
          flatNumber,
          street,
          locality,
          townOrCity,
          county,
          postalCode,
          countryCode,
        },
      }
    );
    return await this.handleResponse(response);
  }
}

export const useCustomerInformationSystemService = (tenantId: string): CustomerInformationSystemClient => {
  return new CustomerInformationSystemClient(tenantId);
};
